import { ElementRef, inject, Injectable } from "@angular/core";
import { HsRailNavButtonGroupModel, HsRailNavButtonModel } from "@hsetu/ngui-components";
import { ICON_HOME, ICON_USER, ICON_CALENDAR, ICON_FILE, ICON_INVOICE, ICON_CALCULATOR, ICON_SHOPPING_CART, ICON_SETTINGS } from "../icons/icon-constants";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import { BOOKING_VIEW_PAGE, CALENDAR_PAGE, CONTACTBOOK_PAGE, HOME_PAGE, INVOICE_PAGE, ITEMSTOCK_PAGE, OFFER_PAGE, PAYMENT_TERMS_PAGE, SETTINGS_PAGE, TASK_PAGE } from "@shared/constants/routing-constants";

@Injectable({
  providedIn: "root",
})
export class RailNavService {
  private readonly _router = inject(Router);

  private _groups: HsRailNavButtonGroupModel[] = [];
  private _bottomGroup: HsRailNavButtonGroupModel | null = null;
  private readonly _buttonRouteMap = new Map<string, string>();

  public build(): { groups: HsRailNavButtonGroupModel[]; bottomGroup: HsRailNavButtonGroupModel } {
    this._buttonRouteMap.clear();

    const grpHome = new HsRailNavButtonGroupModel();
    grpHome.addButton(this.createButton("Home", HOME_PAGE, ICON_HOME));

    const grp1 = new HsRailNavButtonGroupModel();
    grp1.addButton(this.createButton("Kontakte", CONTACTBOOK_PAGE, ICON_USER));
    grp1.addButton(this.createButton("Kalender", CALENDAR_PAGE, ICON_CALENDAR));

    const grp2 = new HsRailNavButtonGroupModel();
    grp2.addButton(this.createButton("Angebote", OFFER_PAGE, ICON_FILE));
    grp2.addButton(this.createButton("Aufträge", TASK_PAGE, ICON_FILE));
    grp2.addButton(this.createButton("Rechnungen", INVOICE_PAGE, ICON_INVOICE));
    grp2.addButton(this.createButton("Offene Posten", BOOKING_VIEW_PAGE, ICON_INVOICE));

    const grp3 = new HsRailNavButtonGroupModel();
    grp3.addButton(this.createButton("Artikel", ITEMSTOCK_PAGE, ICON_SHOPPING_CART));
    grp3.addButton(this.createButton("Konditionen", PAYMENT_TERMS_PAGE, ICON_CALCULATOR));

    const bottomGroup = new HsRailNavButtonGroupModel();
    bottomGroup.addButton(this.createButton("Einstellungen", SETTINGS_PAGE, ICON_SETTINGS));

    this._groups = [grpHome, grp1, grp2, grp3];
    this._bottomGroup = bottomGroup;

    this._router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
      this.syncActiveStateToUrl(e.urlAfterRedirects ?? e.url);
    });

    return { groups: this._groups, bottomGroup };
  }

  public hideRailNav(railNav: ElementRef) {
    const child = Array.from(railNav.nativeElement.children).find((el: HTMLElement) => el.classList.contains("hs-rail-nav-left"));

    if (child) {
      (child as unknown as HTMLElement).style.display = "none";
    }
  }

  public showRailNav(railNav: ElementRef) {
    const child = Array.from(railNav.nativeElement.children).find((el: HTMLElement) => el.classList.contains("hs-rail-nav-left"));
    if (child) {
      (child as unknown as HTMLElement).style.display = "";
    }
  }

  /**
   * Creates a button and registers a route mapping for it.
   * The onClick callback automatically manages active state across all groups
   * including the bottomGroup (workaround for library bug).
   */
  private createButton(title: string, route: string, icon: string): HsRailNavButtonModel {
    const button = new HsRailNavButtonModel(title, () => this.onButtonClicked(button.id), icon, undefined, route);
    this._buttonRouteMap.set(button.id, route);
    return button;
  }

  private syncActiveStateToUrl(url: string): void {
    const allGroups = [...this._groups];
    const allButtons: { group: HsRailNavButtonGroupModel; btn: HsRailNavButtonModel }[] = [];

    for (const group of allGroups) {
      for (const btn of group.buttons) {
        allButtons.push({ group, btn });
      }
    }
    if (this._bottomGroup) {
      for (const btn of this._bottomGroup.buttons) {
        allButtons.push({ group: this._bottomGroup, btn });
      }
    }

    for (const { group, btn } of allButtons) {
      const route = this._buttonRouteMap.get(btn.id);
      const shouldBeActive = route ? url.startsWith("/" + route) || url === "/" + route : false;
      if (btn.isActive !== shouldBeActive) {
        group.updateButtonActive(btn, shouldBeActive);
      }
    }
  }

  /**
   * Sets the active button based on the route.
   * This should be called after programmatic navigation to update the UI.
   */
  public setActiveByRoute(route: string): void {
    // Find button with matching route
    let targetButtonId: string | undefined;
    for (const [buttonId, buttonRoute] of this._buttonRouteMap.entries()) {
      if (buttonRoute === route) {
        targetButtonId = buttonId;
        break;
      }
    }

    if (!targetButtonId) {
      return;
    }

    // Find and activate the button
    let targetButton: HsRailNavButtonModel | undefined;
    let targetGroup: HsRailNavButtonGroupModel | undefined;

    // Search in main groups
    for (const group of this._groups) {
      const btn = group.buttons.find((b) => b.id === targetButtonId);
      if (btn) {
        targetButton = btn;
        targetGroup = group;
        break;
      }
    }

    // Search in bottom group if not found
    if (!targetButton && this._bottomGroup) {
      const btn = this._bottomGroup.buttons.find((b) => b.id === targetButtonId);
      if (btn) {
        targetButton = btn;
        targetGroup = this._bottomGroup;
      }
    }

    if (!targetButton || !targetGroup) {
      return;
    }

    // Deactivate all buttons
    for (const group of this._groups) {
      for (const btn of group.buttons) {
        if (btn.isActive) {
          group.updateButtonActive(btn, false);
        }
      }
    }

    if (this._bottomGroup) {
      for (const btn of this._bottomGroup.buttons) {
        if (btn.isActive) {
          this._bottomGroup.updateButtonActive(btn, false);
        }
      }
    }

    // Re-fetch the target button after deactivation (signal update creates new object references)
    const freshTargetButton = targetGroup.buttons.find((b) => b.id === targetButtonId);
    if (!freshTargetButton) {
      return;
    }

    // Activate target button
    targetGroup.updateButtonActive(freshTargetButton, true);
  }

  private onButtonClicked(buttonId: string) {
    const route = this._buttonRouteMap.get(buttonId);
    if (!route) return;

    this.updateActiveButton(buttonId);
    this._router.navigateByUrl("/", { skipLocationChange: true }).then(() => {
      this._router.navigate([route]);
    });
  }

  private updateActiveButton(buttonId: string): void {
    const isBottomGroupButton = this._bottomGroup?.buttons.some((btn) => btn.id === buttonId) ?? false;

    if (isBottomGroupButton) {
      for (const group of this._groups) {
        for (const btn of group.buttons) {
          if (btn.isActive) group.updateButtonActive(btn, false);
        }
      }
      const clickedBtn = this._bottomGroup!.buttons.find((btn) => btn.id === buttonId);
      if (clickedBtn) this._bottomGroup!.updateButtonActive(clickedBtn, true);
    } else {
      if (this._bottomGroup) {
        for (const btn of this._bottomGroup.buttons) {
          if (btn.isActive) this._bottomGroup.updateButtonActive(btn, false);
        }
      }
      for (const group of this._groups) {
        for (const btn of group.buttons) {
          const shouldBeActive = btn.id === buttonId;
          if (btn.isActive !== shouldBeActive) {
            group.updateButtonActive(btn, shouldBeActive);
          }
        }
      }
    }
  }
}
